import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import TableUI from "../../../components/Basic/TableUI";
import Icon, { ICON_NAME } from "../../../components/Icon";
import Action from "../../../components/Menu/Action";
import { useFilter } from "../../../contexts/FilterContext";
import { openPopup2 } from "../../../contexts/PopupContext";
import { CapabilityService } from "../../../util/services/capability";
import { selectPagePermissions } from "../../../util/store/selectors";
import { durationTxt, firstLetter, showErrorResponse } from "../../../util/varibles/global";
import { ICapability, IServiceTask } from "../../../util/varibles/interface";
import { ACTION } from "../../../util/varibles/permission";
import ServiceTaskForm from "./ServiceTaskForm";

export default function ServiceTasks() {
    const permission = useSelector(selectPagePermissions(ACTION.OPERATION_TASK_BUILDER));
    const [{ loading, data }, setData] = useState<{ loading: boolean, data: IServiceTask[] }>({ loading: false, data: [] });
    const [capabilities, setCapabilities] = useState<ICapability[]>([]);
    const [search] = useFilter(state => state.search);

    useEffect(() => {
        setData(prev => ({ ...prev, loading: true }));
        CapabilityService.serviceTasks()
            .then(rs => {
                setData({ data: rs, loading: false });
            })
            .catch(error => {
                setData(prev => ({ ...prev, loading: false }))
                showErrorResponse("Get service tasks failed", error);
            })
        CapabilityService.gets()
            .then(rs => setCapabilities(rs));
    }, [])

    const handleAdd = (data?: IServiceTask) => {
        let title, onOk, onSend, nameOk;
        if (!data) {
            title = 'New service task';
            onOk = (value: IServiceTask) => setData(prev => ({ ...prev, data: [...prev.data, value] }));
            nameOk = 'Create';
            onSend = CapabilityService.createServiceTask
        } else {
            title = 'Update service task: ' + data.name;
            onOk = (value: IServiceTask) => setData(prev => ({ ...prev, data: prev.data.map(item => item.id === value.id ? value : item) }))
            nameOk = 'Save';
            onSend = CapabilityService.updateServiceTask
        }
        openPopup2(<ServiceTaskForm {...{ data, nameOk, capabilities, onSend, onOk, }} />, { modalProps: { title } })
    }

    const handleAfterDelete = (id: string) => {
        setData(prev => ({ ...prev, data: prev.data.filter(item => item.id !== id) }));
    }

    const columns = [
        {
            key: 'name',
            title: 'Service Task Name',
            dataIndex: 'name',
        },
        {
            key: 'type',
            title: 'Duration',
            dataIndex: 'duration',
            render: (data: number) => durationTxt(data)
        },
        {
            key: 'type',
            title: 'Equipment',
            dataIndex: 'capabilities',
            render: (data: { id: string }[]) => <div>
                {data.map(item => {
                    const { name = "" } = capabilities.find(sub => sub.id === item.id) || {}
                    return firstLetter(name);
                }).join(', ')}
            </div>
        },
        {
            key: 'action',
            width: 60,
            title: ' ',
            render: (data: IServiceTask) => <div className="pl-1 py-1">
                <Action<IServiceTask>
                    data={data}
                    onEdit={permission.update ? () => handleAdd(data) : undefined}
                    onDelete={permission.delete ? CapabilityService.deleteServiceTask : undefined}
                    afterDelete={handleAfterDelete}
                />
            </div>
        }
    ]
    const source = useMemo(() => data.filter(({ name }) => name.toLowerCase().includes((search || "").toLowerCase())), [data, search]);

    if (!permission.view)
        return null

    return <div>
        <div className="flex justify-end h-[52px] items-center">
            <div className="flex gap-1 items-center" onClick={() => handleAdd()}>
                <Icon icon={ICON_NAME.ADD} bg="colorLink" />
                <span className="link">
                    Define new service task
                </span>
            </div>
        </div>
        <TableUI columns={columns} loading={loading} dataSource={source} />
    </div >
}